import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { pad0 } from '../../../utils/number';
import * as classnames from 'classnames';
import { getFromDefaultTemp, getPrimaryContrastText } from './color-utils';
import { intervalEndTimeSum, intervalStartTimeSum, getCorrectIntervalUIValue, getBaxiIntervalSize, getZontIntervalSize, getBaxiIntervalOffset, getZontIntervalOffset, getBaxiDevidedIntervalSize, getZontDevidedIntervalSize, getBaxiIntervalSpacing, getZontIntervalSpacing, } from '../utils';
export const interval_min_size = 20;
const PREFIX = 'Interval';
const classes = {
    grid_background: `${PREFIX}-grid_background`,
    root: `${PREFIX}-root`,
    less_radius: `${PREFIX}-less_radius`,
    title: `${PREFIX}-title`,
    select_interval: `${PREFIX}-select_interval`,
    with_border: `${PREFIX}-with_border`,
    selected_border: `${PREFIX}-selected_border`,
    label_background: `${PREFIX}-label_background`,
    selected_label: `${PREFIX}-selected_label`,
};
const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.grid_background}`]: {
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(45deg,rgba(0, 0, 0, 0) 48%,rgba(255, 255, 255, 0.7) 50%,rgba(0, 0, 0, 0) 52%),linear-gradient(-45deg,rgba(0, 0, 0, 0) 48%,rgba(255, 255, 255, 0.7) 50%,rgba(0, 0, 0, 0) 52%)'
            : 'linear-gradient(45deg,rgba(0, 0, 0, 0) 48%,rgba(0, 0, 0, 0.2) 50%,rgba(0, 0, 0, 0) 52%),linear-gradient(-45deg,rgba(0, 0, 0, 0) 48%,rgba(0, 0, 0, 0.2) 50%,rgba(0, 0, 0, 0) 52%)',
        backgroundSize: '1em 1em',
        backgroundColor: theme.palette.mode === 'dark' ? '#303030' : '#fff',
    },
    [`& .${classes.root}`]: {
        zIndex: 2,
        boxSizing: 'border-box',
        padding: 2,
        //скругление
        borderRadius: 12,
    },
    [`& .${classes.less_radius}`]: {
        borderRadius: 8,
    },
    [`& .${classes.title}`]: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        lineHeight: 1,
    },
    [`& .${classes.select_interval}`]: {
        borderWidth: 0,
        outlineWidth: 2,
        outlineColor: theme.palette.primary.main,
        outlineStyle: 'solid',
        zIndex: 10,
    },
    [`& .${classes.with_border}`]: {
        borderTopStyle: 'solid',
        borderTopWidth: 1,
        borderColor: theme.palette.mode === 'dark' ? '#303030' : '#e0e0e0',
    },
    [`& .${classes.selected_border}`]: {
        borderColor: theme.palette.primary.main,
        zIndex: 10,
    },
    [`& .${classes.label_background}`]: {
        background: 'white',
        zIndex: 1,
    },
    [`& .${classes.selected_label}`]: {
        background: theme.palette.common.white,
        borderRadius: 12,
        '& p': {
            color: theme.palette.primary.main,
        },
        zIndex: 11,
    },
}));
export const Interval = memo(function Interval(props) {
    const { interval, interval_id, heating_modes, empty, onSelectInterval, week_view, selected, selected_hc, temp_or_mode, size, offset, is_baxi_app, } = props;
    const { height, width } = size;
    const { top, left } = offset;
    const { start_sum, end_sum } = useMemo(() => {
        return {
            start_sum: intervalStartTimeSum(interval),
            end_sum: intervalEndTimeSum(interval),
        };
    }, [interval]);
    const { color, temp_str } = useMemo(() => {
        return getFromDefaultTemp(heating_modes, 'temp_or_mode' in interval ? interval.temp_or_mode : temp_or_mode, selected_hc);
    }, [heating_modes, interval, selected_hc, temp_or_mode]);
    const onClickInterval = useCallback(() => {
        onSelectInterval?.(interval_id, interval);
    }, [interval, onSelectInterval]);
    const interval_size = useMemo(() => getCorrectIntervalUIValue(getBaxiIntervalSize(height, !week_view), getZontIntervalSize(width, !week_view), is_baxi_app), [size, week_view]);
    const interval_offset = useMemo(() => getCorrectIntervalUIValue(getBaxiIntervalOffset(top, !week_view), getZontIntervalOffset(left, !week_view), is_baxi_app), [week_view, offset]);
    const devided_interval_size = useMemo(() => getCorrectIntervalUIValue(getBaxiDevidedIntervalSize(), getZontDevidedIntervalSize(), is_baxi_app), [is_baxi_app, week_view]);
    const show_interval_delimeter = useMemo(() => start_sum !== 0 && !week_view && is_baxi_app, [start_sum, week_view, is_baxi_app]);
    const show_selected_interval_delimeter = useMemo(() => end_sum < 1439 && !week_view && selected && is_baxi_app, [end_sum, week_view, selected, is_baxi_app]);
    const intervals_start_delimeter_time = useMemo(() => `${pad0(interval.start_hour, 2)} : ${pad0(interval.start_min, 2)}`, [interval]);
    const intervals_end_delimeter_time = useMemo(() => `${pad0(interval.end_hour, 2)} : ${pad0(interval.end_min, 2)}`, [interval]);
    const devided_interval_spacing = useMemo(() => getCorrectIntervalUIValue(getBaxiIntervalSpacing(start_sum === 0, end_sum === 1439, week_view), getZontIntervalSpacing(start_sum === 0 || !is_baxi_app, end_sum === 1439 || !is_baxi_app, week_view), is_baxi_app), [start_sum, end_sum, is_baxi_app, week_view]);
    const interval_text = useMemo(() => {
        if (week_view && is_baxi_app) {
            return temp_str.slice(0, 5);
        }
        return temp_str;
    }, [week_view, temp_str, is_baxi_app]);
    return (_jsxs(StyledBox, { position: "absolute", width: interval_size.width, left: interval_offset.left, top: interval_offset.top, height: interval_size.height, minHeight: !week_view ? interval_min_size : undefined, minWidth: !week_view ? interval_min_size : undefined, children: [show_interval_delimeter && (_jsxs(Box, { position: "relative", width: "100%", top: "-8px", display: "flex", alignItems: "center", justifyContent: "center", children: [_jsx(Box, { px: 1, className: classnames(classes.label_background, {
                            [classes.selected_label]: selected,
                        }), children: _jsx(Typography, { variant: "body2", style: { lineHeight: 1 }, color: "textSecondary", children: intervals_start_delimeter_time }) }), _jsx(Box, { position: "absolute", top: "50%", width: "100%", className: classnames(classes.with_border, {
                            [classes.selected_border]: selected,
                        }) })] })), _jsx(Box, { className: classnames(classes.root, {
                    [classes.grid_background]: empty,
                    [classes.less_radius]: week_view,
                    [classes.select_interval]: selected,
                }), position: "absolute", width: devided_interval_size.width, height: devided_interval_size.height, style: {
                    backgroundColor: color,
                }, top: devided_interval_spacing.top, bottom: devided_interval_spacing.bottom, left: devided_interval_spacing.left, right: devided_interval_spacing.right, display: "flex", margin: 'auto', alignItems: "center", justifyContent: "center", flexDirection: "column", onClick: onClickInterval, children: _jsx(Typography, { className: classes.title, variant: week_view ? 'body2' : 'h6', style: {
                        color: getPrimaryContrastText(color),
                        overflow: 'visible',
                    }, children: interval_text }) }), show_selected_interval_delimeter && (_jsxs(Box, { position: "absolute", width: "100%", right: "50%", bottom: "-6px", display: "flex", alignItems: "center", justifyContent: "center", children: [_jsx(Box, { px: 1, className: classnames(classes.label_background, {
                            [classes.selected_label]: selected,
                        }), children: _jsx(Typography, { variant: "body2", style: { lineHeight: 1 }, color: "textSecondary", children: intervals_end_delimeter_time }) }), _jsx(Box, { position: "absolute", top: "50%", width: "100%", className: classnames(classes.with_border, {
                            [classes.selected_border]: selected,
                        }) })] }))] }));
});
